import type { FileMap } from "@buildlab/shared";
import { bridgeScript } from "./bridge.js";
import { addRunCounters } from "@buildlab/codeview";
import { addLoopGuards } from "./loop-guard.js";

// Builds the document shown in the preview iframe (PRD FR-12 to FR-17).
// The app renders it with <iframe sandbox="allow-scripts" srcdoc=...>; nothing else is allowed.

export const PREVIEW_SANDBOX = "allow-scripts";

export interface PreviewOptions {
  /** Origin of the curated asset library, e.g. https://assets.chahomework.com. Omit for none. */
  assetsOrigin?: string;
  /** Values saved with window.saveData earlier in this session. */
  savedData?: Record<string, unknown>;
  loopBudgetMs?: number;
  /** Count function runs for the code view (each body calls __blRan; see @buildlab/codeview). */
  trace?: boolean;
  /** Which page of a multi-page site to show (default index.html). */
  page?: string;
}

export interface PreviewBuild {
  html: string;
  /** Things the builder removed or couldn't find, shown in the console panel. */
  warnings: string[];
  guardedLoops: number;
}

export function previewCsp(assetsOrigin?: string): string {
  const assets = assetsOrigin ? ` ${new URL(assetsOrigin).origin}` : "";
  return [
    "default-src 'none'",
    "script-src 'unsafe-inline' 'unsafe-eval'",
    "style-src 'unsafe-inline'",
    `img-src data: blob:${assets}`,
    `media-src data: blob:${assets}`,
    "font-src data:",
    "connect-src 'none'",
    "form-action 'none'",
    "frame-src 'none'",
    "worker-src 'none'",
    "base-uri 'none'",
  ].join("; ");
}

const isRemote = (url: string) => /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(url.trim());
const attr = (tag: string, name: string): string | undefined => {
  const m = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, "i").exec(tag);
  return m ? (m[1] ?? m[2] ?? m[3]) : undefined;
};
const escapeHtmlAttr = (s: string) => s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
/** Keep inlined code from closing its own <script>/<style> element early. */
const escapeScript = (js: string) => js.replace(/<\/(script)/gi, "<\\/$1").replace(/<!--/g, "<\\!--");
const escapeStyle = (css: string) => css.replace(/<\/(style)/gi, "<\\/$1");

/** Every page of a multi-page site (none for a one-page project), capped so the preview stays small. */
function sitePages(files: FileMap): Record<string, string> {
  const names = Object.keys(files).filter((f) => /\.html?$/i.test(f)).sort();
  if (names.length < 2) return {};
  const out: Record<string, string> = {};
  let budget = 200_000;
  for (const n of names) {
    const html = files[n] ?? "";
    if (html.length > budget) break;
    out[n] = html;
    budget -= html.length;
  }
  return out;
}

export function buildPreview(files: FileMap, options: PreviewOptions = {}): PreviewBuild {
  const warnings: string[] = [];
  let guardedLoops = 0;
  const page = options.page && /\.html?$/i.test(options.page) && files[options.page] !== undefined ? options.page : "index.html";
  const source = files[page] ?? "";
  if (!files[page]) warnings.push("This project has no index.html, so there's nothing to show yet.");

  const guard = (code: string, name: string) => {
    const r = addLoopGuards(code, name, options.loopBudgetMs);
    guardedLoops += r.guardedLoops;
    return r.code;
  };

  const transform = (fragment: string): string => {
    let inline = 0;
    return (
      fragment
        // Student HTML can't set its own base URL, refresh/redirect, or CSP.
        .replace(/<base\b[^>]*>/gi, () => (warnings.push("Removed a <base> tag."), ""))
        .replace(/<meta\b[^>]*http-equiv[^>]*>/gi, () => (warnings.push("Removed a <meta http-equiv> tag."), ""))
        .replace(/<link\b[^>]*>/gi, (tag) => {
          const rel = (attr(tag, "rel") ?? "").toLowerCase();
          const href = attr(tag, "href") ?? "";
          if (!rel.split(/\s+/).includes("stylesheet")) return "";
          if (isRemote(href)) {
            warnings.push(`Removed ${href}: previews can't load files from the internet.`);
            return "";
          }
          const css = files[href];
          if (css === undefined) {
            warnings.push(`${page} links to ${href}, but there's no file with that name.`);
            return "";
          }
          return `<style>${escapeStyle(css)}\n/*# sourceURL=${href} */</style>`;
        })
        .replace(/<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi, (_whole, attrs: string, body: string) => {
          const src = attr(`<script ${attrs}>`, "src");
          const type = attr(`<script ${attrs}>`, "type");
          const typeAttr = type ? ` type="${escapeHtmlAttr(type)}"` : "";
          if (src !== undefined) {
            if (isRemote(src)) {
              warnings.push(`Removed ${src}: previews can't load scripts from the internet.`);
              return "";
            }
            const js = files[src];
            if (js === undefined) {
              warnings.push(`${page} loads ${src}, but there's no file with that name.`);
              return "";
            }
            const counted = options.trace ? addRunCounters(js, src).code : js;
            return `<script${typeAttr}>${escapeScript(guard(counted, src))}\n//# sourceURL=${src}\n</script>`;
          }
          inline++;
          return `<script${typeAttr}>${escapeScript(guard(body, `${page} script ${inline}`))}</script>`;
        })
    );
  };

  const headMatch = /<head\b[^>]*>([\s\S]*?)<\/head\s*>/i.exec(source);
  const bodyMatch = /<body\b([^>]*)>([\s\S]*?)(?:<\/body\s*>|$)/i.exec(source);
  const headInner = headMatch?.[1] ?? "";
  let bodyAttrs = "";
  let bodyInner: string;
  if (bodyMatch) {
    bodyAttrs = bodyMatch[1] ?? "";
    bodyInner = bodyMatch[2] ?? "";
  } else {
    bodyInner = source
      .replace(/<!doctype[^>]*>/gi, "")
      .replace(/<head\b[^>]*>[\s\S]*?<\/head\s*>/i, "")
      .replace(/<\/?html\b[^>]*>/gi, "");
  }
  const lang = attr(/<html\b[^>]*>/i.exec(source)?.[0] ?? "", "lang") ?? "en";

  const html =
    `<!doctype html><html lang="${escapeHtmlAttr(lang)}"><head>` +
    `<meta http-equiv="Content-Security-Policy" content="${escapeHtmlAttr(previewCsp(options.assetsOrigin))}">` +
    `<meta charset="utf-8">` +
    `<script>${escapeScript(bridgeScript(options.savedData, sitePages(files)))}</script>` +
    transform(headInner) +
    `</head><body${bodyAttrs}>` +
    transform(bodyInner) +
    `</body></html>`;

  return { html, warnings, guardedLoops };
}
